import HeadingSmall from '@/components/layout/HeadingSmall';
import InputError from '@/components/layout/InputError';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import SettingsLayout from '@/layouts/settings/layout';
import { type BreadcrumbItem, type SharedData } from '@/types';
import { Transition } from '@headlessui/react';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { AtSign, BadgeEuro, Bug, Calculator, CalendarDays, ClipboardList, FileSignature, RefreshCw, UserCheck } from 'lucide-react';
import React, { FormEventHandler } from 'react';
import { route } from 'ziggy-js';

const NOTIFICATION_TYPES = [
    'mentions',
    'work_order_assignment',
    'work_order_created',
    'bug_reports',
    'vacation_request',
    'status_change',
    'calculation_request_created',
    'calculation_request_status_change',
    'service_request_approval',
    'lead_offer_approval',
] as const;
type NotificationType = (typeof NOTIFICATION_TYPES)[number];
type NotificationPrefs = Record<NotificationType, { email: boolean; in_app: boolean }>;

const NOTIFICATION_ICONS: Record<NotificationType, React.ReactNode> = {
    mentions: <AtSign className="h-5 w-5" />,
    work_order_assignment: <UserCheck className="h-5 w-5" />,
    work_order_created: <ClipboardList className="h-5 w-5" />,
    bug_reports: <Bug className="h-5 w-5" />,
    vacation_request: <CalendarDays className="h-5 w-5" />,
    status_change: <RefreshCw className="h-5 w-5" />,
    calculation_request_created: <Calculator className="h-5 w-5" />,
    calculation_request_status_change: <RefreshCw className="h-5 w-5" />,
    service_request_approval: <FileSignature className="h-5 w-5" />,
    lead_offer_approval: <BadgeEuro className="h-5 w-5" />,
};

const breadcrumbs: BreadcrumbItem[] = [
    {
        title: 'Profile settings',
        href: '/settings/profile',
    },
];

interface ProfileForm {
    first_name: string;
    last_name: string;
    email: string;
    locale: string;
    notification_preferences: NotificationPrefs;
}

export default function Profile({ mustVerifyEmail, status }: { mustVerifyEmail: boolean; status?: string }) {
    const { auth } = usePage<SharedData>().props;
    const { t } = useTranslations();

    const defaultPrefs: NotificationPrefs = {
        mentions: { email: true, in_app: true },
        work_order_assignment: { email: true, in_app: true },
        work_order_created: { email: true, in_app: true },
        bug_reports: { email: true, in_app: true },
        vacation_request: { email: true, in_app: true },
        status_change: { email: true, in_app: true },
        calculation_request_created: { email: true, in_app: true },
        calculation_request_status_change: { email: true, in_app: true },
        service_request_approval: { email: true, in_app: true },
        lead_offer_approval: { email: true, in_app: true },
    };

    const storedPrefs = (auth.user.notification_preferences ?? {}) as Partial<NotificationPrefs>;
    const mergedPrefs = NOTIFICATION_TYPES.reduce((acc, type) => {
        acc[type] = {
            email: storedPrefs[type]?.email ?? defaultPrefs[type].email,
            in_app: storedPrefs[type]?.in_app ?? defaultPrefs[type].in_app,
        };
        return acc;
    }, {} as NotificationPrefs);

    const { data, setData, patch, errors, processing, recentlySuccessful } = useForm<Required<ProfileForm>>({
        first_name: auth.user.first_name,
        last_name: auth.user.last_name,
        email: auth.user.email,
        locale: auth.user.locale ?? 'en',
        notification_preferences: mergedPrefs,
    });

    const togglePref = (type: NotificationType, channel: 'email' | 'in_app', value: boolean) => {
        setData('notification_preferences', {
            ...data.notification_preferences,
            [type]: {
                ...data.notification_preferences[type],
                [channel]: value,
            },
        });
    };

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        patch(route('settings.profile.update'), {
            preserveScroll: true,
        });
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('settings.profile.title', 'Profile settings')} />

            <SettingsLayout>
                <div className="space-y-6">
                    <HeadingSmall
                        title={t('settings.profile.information_title', 'Profile information')}
                        description={t('settings.profile.information_description', 'Update your name and email address')}
                    />

                    <form onSubmit={submit} className="space-y-6">
                        <div className="grid gap-2">
                            <Label htmlFor="first_name">{t('settings.profile.first_name', 'First Name')}</Label>

                            <Input
                                id="first_name"
                                className="mt-1 block w-full"
                                value={data.first_name}
                                onChange={(e) => setData('first_name', e.target.value)}
                                required
                                autoComplete="first_name"
                                placeholder={t('settings.profile.first_name_placeholder', 'First name')}
                                disabled={!!auth.user.azure_id}
                            />

                            {auth.user.azure_id && (
                                <p className="text-muted-foreground text-sm">
                                    {t(
                                        'settings.profile.azure_name_hint',
                                        'Name cannot be changed for Microsoft-authenticated accounts. Please update your name through Microsoft account settings.',
                                    )}
                                </p>
                            )}

                            <InputError className="mt-2" message={errors.first_name} />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="last_name">{t('settings.profile.last_name', 'Last Name')}</Label>

                            <Input
                                id="last_name"
                                className="mt-1 block w-full"
                                value={data.last_name}
                                onChange={(e) => setData('last_name', e.target.value)}
                                required
                                autoComplete="family-name"
                                placeholder={t('settings.profile.last_name_placeholder', 'Last name')}
                                disabled={!!auth.user.azure_id}
                            />

                            {auth.user.azure_id && (
                                <p className="text-muted-foreground text-sm">
                                    {t(
                                        'settings.profile.azure_name_hint',
                                        'Name cannot be changed for Microsoft-authenticated accounts. Please update your name through Microsoft account settings.',
                                    )}
                                </p>
                            )}

                            <InputError className="mt-2" message={errors.last_name} />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="email">{t('settings.profile.email', 'Email address')}</Label>

                            <Input
                                id="email"
                                type="email"
                                className="mt-1 block w-full"
                                value={data.email}
                                onChange={(e) => setData('email', e.target.value)}
                                required
                                autoComplete="username"
                                placeholder={t('settings.profile.email_placeholder', 'Email address')}
                                disabled={!!auth.user.azure_id}
                            />

                            {auth.user.azure_id && (
                                <p className="text-muted-foreground text-sm">
                                    {t(
                                        'settings.profile.azure_email_hint',
                                        'Email cannot be changed for Microsoft-authenticated accounts. Please update your email through Microsoft account settings.',
                                    )}
                                </p>
                            )}

                            <InputError className="mt-2" message={errors.email} />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="locale">{t('settings.profile.language', 'Language')}</Label>
                            <select
                                id="locale"
                                className="border-input bg-background focus-visible:ring-ring mt-1 block w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                                value={data.locale}
                                onChange={(e) => setData('locale', e.target.value)}
                            >
                                <option value="en">{t('settings.profile.language_en', 'English')}</option>
                                <option value="lt">{t('settings.profile.language_lt', 'Lietuvių')}</option>
                            </select>
                            <p className="text-muted-foreground text-sm">
                                {t('settings.profile.language_help', 'This changes the language of the application interface for your account.')}
                            </p>
                            <InputError className="mt-2" message={errors.locale} />
                        </div>

                        <div className="border-t pt-6">
                            <HeadingSmall
                                title={t('settings.profile.notifications_title', 'Notification preferences')}
                                description={t('settings.profile.notifications_description', 'Choose how you want to be notified for each event.')}
                            />

                            <div className="mt-4 overflow-hidden rounded-xl border">
                                {/* Header row */}
                                <div className="bg-muted/40 grid grid-cols-[1fr_88px_88px] items-center border-b px-4 py-2">
                                    <div />
                                    <span className="text-muted-foreground text-center text-xs font-medium">
                                        {t('settings.profile.notifications_email', 'Email')}
                                    </span>
                                    <span className="text-muted-foreground text-center text-xs font-medium">
                                        {t('settings.profile.notifications_in_app', 'In-app')}
                                    </span>
                                </div>

                                {/* Notification rows */}
                                {NOTIFICATION_TYPES.map((type, idx) => (
                                    <div
                                        key={type}
                                        className={`grid grid-cols-[1fr_88px_88px] items-center px-4 py-4 ${idx < NOTIFICATION_TYPES.length - 1 ? 'border-b' : ''}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="bg-muted text-muted-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                                                {NOTIFICATION_ICONS[type]}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold">{t(`settings.profile.notifications_${type}`, type)}</p>
                                                <p className="text-muted-foreground text-xs">
                                                    {t(`settings.profile.notifications_${type}_description`, '')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex justify-center">
                                            <Switch
                                                checked={data.notification_preferences[type].email}
                                                onCheckedChange={(v) => togglePref(type, 'email', v)}
                                            />
                                        </div>
                                        <div className="flex justify-center">
                                            <Switch
                                                checked={data.notification_preferences[type].in_app}
                                                onCheckedChange={(v) => togglePref(type, 'in_app', v)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {mustVerifyEmail && auth.user.email_verified_at === null && (
                            <div>
                                <p className="text-muted-foreground -mt-4 text-sm">
                                    {t('settings.profile.email_unverified', 'Your email address is unverified.')}{' '}
                                    <Link
                                        href={route('verification.send')}
                                        method="post"
                                        as="button"
                                        className="text-foreground underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 ease-out hover:decoration-current! dark:decoration-neutral-500"
                                    >
                                        {t('settings.profile.resend_verification', 'Click here to resend the verification email.')}
                                    </Link>
                                </p>

                                {status === 'verification-link-sent' && (
                                    <div className="text-success-subtle-foreground mt-2 text-sm font-medium">
                                        {t('settings.profile.verification_link_sent', 'A new verification link has been sent to your email address.')}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-center gap-4">
                            <Button disabled={processing}>{t('common.save', 'Save')}</Button>

                            <Transition
                                show={recentlySuccessful}
                                enter="transition ease-in-out"
                                enterFrom="opacity-0"
                                leave="transition ease-in-out"
                                leaveTo="opacity-0"
                            >
                                <p className="text-muted-foreground text-sm">{t('common.saved', 'Saved')}</p>
                            </Transition>
                        </div>
                    </form>
                </div>
            </SettingsLayout>
        </AppLayout>
    );
}
